/**
 * Runs Capacitor-only side effects when the app boots inside the native
 * iOS / Android shell (no-op on web). Import once from App.js and render
 * it near the root — it renders nothing but its `useEffect` fires the
 * StoreKit / Play Billing initialization + hides the splash after login.
 */
import { useEffect } from "react";
import { isNativeApp, isNativeIOS } from "@/lib/platform";
import IAP from "@/lib/iap";
import { loadVoices } from "@/lib/tts";

export default function CapacitorBootstrap() {
  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;

    // Tag <body> so global CSS can opt in to native-only rules
    // (overscroll disable, no-tap-highlight, no image callout, etc).
    document.body.classList.add("capacitor-native");
    if (isNativeIOS()) document.body.classList.add("ios");
    else document.body.classList.add("android");

    (async () => {
      // Splash: hide as soon as React has painted.
      try {
        const c = window.Capacitor;
        const splash = c?.Plugins?.SplashScreen;
        if (splash?.hide) await splash.hide();
      } catch { /* noop */ }

      // Status bar: use light content on our navy header (iOS only —
      // Android uses system UI colors from the theme).
      if (isNativeIOS()) {
        try {
          const sb = window.Capacitor?.Plugins?.StatusBar;
          if (sb?.setStyle) await sb.setStyle({ style: "LIGHT" });
        } catch { /* noop */ }
      }

      // IAP: initialize the plugin so products load in the background.
      try {
        await IAP.init();
      } catch (e) {
        console.warn("[Capacitor] IAP init failed:", e);
      }
      if (cancelled) return;

      // TTS: warm up the native text-to-speech plugin. Its Swift `init()`
      // configures AVAudioSession on a background dispatch queue, so the
      // very first `speak()` fired from a review screen can race the
      // session-activation and play no audio. Calling `getSupportedVoices()`
      // here forces the plugin to load synchronously and gives the audio
      // session ~a-few-hundred-ms to activate before the student ever
      // presses "Read Aloud".
      try {
        await loadVoices(2000);
      } catch (e) {
        console.warn("[Capacitor] TTS warm-up failed:", e);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return null;
}
